import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
    ConfiguratorMutationContextType,
    ConfiguratorMutationProvider,
    SelectOptionParams,
} from '@/components/OptionConfigurator/ConfiguratorMutationContext';
import { useOptionConfigurator } from '@/components/OptionConfigurator/OptionConfiguratorContext';
import { useParams } from 'react-router-dom';
import {
    calculateShowroom,
    ShowroomCalculateRequest,
    ShowroomData,
    showroomKeys,
    ShowroomOptionSelection,
    ShowroomPathParams,
    ShowroomPresetSelection,
} from '@/services/showroom';
import { ConfiguratorData } from '@/services/configurator';
import { Toast } from '@ygtechnologies/bygr-frontend-component-library';
import { t } from '@lingui/core/macro';
import { useShowroomSelections } from './useShowroomSelections';

interface ShowroomMutationProviderProps {
    pathParams: ShowroomPathParams;
    children: React.ReactNode;
}

function normalizeRoom(roomId: number | null | undefined): number | null {
    return roomId == null ? null : roomId;
}

function replaceOption(list: ShowroomOptionSelection[], next: ShowroomOptionSelection): ShowroomOptionSelection[] {
    const filtered = list.filter((s) => !(s.groupId === next.groupId && s.roomId === next.roomId));
    return [...filtered, next];
}

// Implements ConfiguratorMutationContext for the public showroom: option/style picks are written
// to localStorage (via useShowroomSelections) and then the full selection state is sent to the
// backend `/calculate` endpoint, which returns a recalculated ConfiguratorData. The query cache
// for this resource is updated optimistically via setQueryData so all consumers re-render
// against the fresh result.
export const ShowroomMutationProvider: React.FC<ShowroomMutationProviderProps> = ({ pathParams, children }) => {
    const queryClient = useQueryClient();
    const { selectedDeadlineId, projectData, multiDeadline } = useOptionConfigurator();
    // The legacy configurator AND the multi-deadline preview show every deadline at once, so they
    // must recalculate against all of them: a null deadlineId tells the backend not to filter the
    // response to a single deadline (the GET already loads with null). The standard v2 flow
    // navigates one deadline at a time and keeps filtering.
    const calcDeadlineId = projectData?.useLegacyOptionsView || multiDeadline ? null : selectedDeadlineId;
    const { roomId: urlRoomId } = useParams<{ roomId?: string }>();
    // Storage key is stable per (unitId, hash) — projectSlug is cosmetic.
    const storageKey = `${pathParams.unitId}-${pathParams.hash}`;
    const { selections, setOption, setOptions, removeOption, setPreset, removePreset } =
        useShowroomSelections(storageKey);

    // Track which kind of mutation is in flight so the existing isSelecting / isPickingStyle /
    // isUnpickingStyle flags map naturally onto a single `calculate` request.
    const [activeKind, setActiveKind] = useState<'option' | 'pick' | 'unpick' | null>(null);

    // `/calculate` requests are fire-and-forget and can overlap — a click landing while the mount-time
    // replay (or a previous click) is still in flight, or two quick picks. Responses are not
    // guaranteed to arrive in send order, and each one overwrites the whole cached configurator, so a
    // late-arriving OLD response would wipe a newer pick and make the option appear to deselect
    // itself. Stamp every request and apply only responses that are still the most recent.
    const requestSeqRef = useRef(0);
    const appliedSeqRef = useRef(0);

    const calculateMutation = useMutation({
        mutationFn: ({ request }: { request: ShowroomCalculateRequest; seq: number }) =>
            calculateShowroom(pathParams, request),
        onSuccess: (resp, { seq }) => {
            // Superseded by a newer request — its response is the authoritative one, drop this.
            if (seq < appliedSeqRef.current) return;
            appliedSeqRef.current = seq;
            // The configurator data is cached under byUnit(unitId, hash) — patch the cached
            // ShowroomData so all consumers re-render with fresh prices/selected flags. This is the
            // authoritative server state; it supersedes the optimistic patch applied at click time.
            queryClient.setQueryData<ShowroomData>(
                showroomKeys.byUnit(pathParams.unitId, pathParams.hash),
                (old) => (old ? { ...old, configuratorData: resp.configuratorData } : old)
            );
        },
        // A failed/slow /calculate must not silently revert the pick or bubble up as an unhandled
        // rejection (which the global Sentry handler would otherwise capture). The selection stays
        // in localStorage and in the optimistic cache patch, so it survives until the next
        // successful recalculation (e.g. on reload). Just surface a toast.
        onError: () => {
            Toast.error(t`Could not update your selection. Please try again.`);
        },
        onSettled: () => setActiveKind(null),
    });

    // Optimistically reflect option picks in the cached configurator data so the UI updates
    // immediately and doesn't appear to "revert" while /calculate is in flight (or if it fails).
    // Marks the chosen option selected within its (group, room); the authoritative prices/flags
    // arrive in onSuccess. Keyed on (id, roomId) so multi-room groups are patched per room.
    const patchSelectedOptions = (optionSelections: ShowroomOptionSelection[]) => {
        const byKey = new Map(optionSelections.map((s) => [`${s.groupId}:${s.roomId ?? 'null'}`, s.optionId]));
        queryClient.setQueryData<ShowroomData>(
            showroomKeys.byUnit(pathParams.unitId, pathParams.hash),
            (old) => {
                if (!old?.configuratorData) return old;
                const groups = old.configuratorData.groups.map((g) => {
                    const selectedId = byKey.get(`${g.id}:${g.roomId ?? 'null'}`);
                    if (selectedId === undefined) return g;
                    const options = g.options.map((o) => ({ ...o, selected: o.id === selectedId }));
                    return { ...g, options, selectedOption: options.find((o) => o.selected) ?? g.selectedOption };
                });
                return { ...old, configuratorData: { ...old.configuratorData, groups } };
            }
        );
    };

    // Optimistically clear a group's selection so the UI reflects "none selected" immediately while
    // /calculate is in flight; the authoritative state arrives in onSuccess.
    const patchClearedOption = (groupId: number, roomId: number | null) => {
        queryClient.setQueryData<ShowroomData>(
            showroomKeys.byUnit(pathParams.unitId, pathParams.hash),
            (old) => {
                if (!old?.configuratorData) return old;
                const groups = old.configuratorData.groups.map((g) => {
                    if (!(g.id === groupId && (g.roomId ?? null) === roomId)) return g;
                    const options = g.options.map((o) => ({ ...o, selected: false }));
                    return { ...g, options, selectedOption: null };
                });
                return { ...old, configuratorData: { ...old.configuratorData, groups } };
            }
        );
    };

    // Replay any selections persisted in localStorage on page load. Without this, the initial
    // GET /api/showroom/... returns the backend's default state (no selections applied), so even
    // if the user previously picked options they'd render as un-selected after a refresh.
    // Ref keyed by storageKey to ensure we only replay once per resource; otherwise the cache
    // update in onSuccess re-triggers this effect and we'd loop.
    const replayedForKeyRef = useRef<string | null>(null);
    useEffect(() => {
        if (replayedForKeyRef.current === storageKey) return;
        const hasSaved = selections.optionSelections.length > 0 || selections.presetSelections.length > 0;
        if (!hasSaved) {
            replayedForKeyRef.current = storageKey;
            return;
        }
        replayedForKeyRef.current = storageKey;
        setActiveKind('option');
        requestSeqRef.current += 1;
        calculateMutation.mutate({
            seq: requestSeqRef.current,
            request: {
                deadlineId: calcDeadlineId,
                optionSelections: selections.optionSelections,
                presetSelections: selections.presetSelections,
            },
        });
    }, [storageKey, calcDeadlineId, selections, calculateMutation]);

    const triggerCalculate = (
        nextOptions: ShowroomOptionSelection[],
        nextPresets: ShowroomPresetSelection[],
        kind: 'option' | 'pick' | 'unpick'
    ) => {
        setActiveKind(kind);
        requestSeqRef.current += 1;
        calculateMutation.mutate({
            seq: requestSeqRef.current,
            request: {
                deadlineId: calcDeadlineId,
                optionSelections: nextOptions,
                presetSelections: nextPresets,
            },
        });
    };

    const resolveRoom = (override: number | null | undefined): number | null => {
        if (override !== undefined) return normalizeRoom(override);
        if (!urlRoomId) return null;
        return urlRoomId === '0' ? null : Number(urlRoomId);
    };

    const value = useMemo<ConfiguratorMutationContextType>(() => {
        const applySelectOption = (params: SelectOptionParams): ShowroomOptionSelection[] => {
            const room = resolveRoom(params.roomId);
            const next: ShowroomOptionSelection = {
                groupId: params.option.optionGroupId,
                roomId: room,
                optionId: params.option.id,
                quantity: params.quantity ?? null,
            };
            // Persist to localStorage AND return the merged list so the caller can post it before
            // React re-renders with the new state.
            setOption(next.groupId, next.roomId, next.optionId, next.quantity);
            return replaceOption(selections.optionSelections, next);
        };

        return {
            selectOption: (params) => {
                const nextOptions = applySelectOption(params);
                patchSelectedOptions(nextOptions);
                triggerCalculate(nextOptions, selections.presetSelections, 'option');
            },
            clearSelection: (params) => {
                const room = resolveRoom(params.roomId);
                const next = removeOption(params.groupId, room);
                patchClearedOption(params.groupId, room);
                triggerCalculate(next.optionSelections, selections.presetSelections, 'option');
            },
            selectOptions: async (paramsList) => {
                // Apply the whole batch in one go. setOptions folds over the running list, so every
                // entry survives (the previous per-item loop rebuilt from a stale snapshot and kept
                // only the last). Each selection is stamped with its group's deadline for the legacy
                // multi-deadline page; the v2 flow omits it.
                const next = setOptions(
                    paramsList.map((p) => ({
                        groupId: p.option.optionGroupId,
                        roomId: resolveRoom(p.roomId),
                        optionId: p.option.id,
                        deadlineId: p.deadlineId ?? selectedDeadlineId,
                        quantity: p.quantity ?? null,
                    }))
                );
                patchSelectedOptions(next.optionSelections);
                await new Promise<void>((resolve, reject) => {
                    setActiveKind('option');
                    requestSeqRef.current += 1;
                    calculateMutation.mutate(
                        {
                            seq: requestSeqRef.current,
                            request: {
                                deadlineId: calcDeadlineId,
                                optionSelections: next.optionSelections,
                                presetSelections: next.presetSelections,
                            },
                        },
                        {
                            onSuccess: () => resolve(),
                            onError: (e) => reject(e),
                        }
                    );
                });
            },
            isSelecting: calculateMutation.isPending && activeKind === 'option',
            pickStyle: (styleId) => {
                // Resolve the style group from the cached configurator data.
                const showroomData = queryClient.getQueryData<{ configuratorData: ConfiguratorData }>(
                    showroomKeys.byUnit(pathParams.unitId, pathParams.hash)
                );
                const styleGroup = showroomData?.configuratorData?.styleGroups.find((g) =>
                    g.styles.some((s) => s.id === styleId)
                );
                if (!styleGroup) return;
                const groupId = styleGroup.id;
                // Room scope comes from the style group itself: null = main style, an id = a
                // room-scoped style. The URL/arg room would mis-scope a main style picked from a room.
                const room = styleGroup.roomId ?? null;
                setPreset(groupId, room, styleId);
                const nextPresets: ShowroomPresetSelection[] = [
                    ...selections.presetSelections.filter((s) => !(s.presetGroupId === groupId && s.roomId === room)),
                    { presetGroupId: groupId, roomId: room, selectedPresetId: styleId },
                ];
                // Manual picks are intentionally kept in storage; the backend drops any that the new
                // style disallows (GetSelectedOption) so covered groups adopt the style's default.
                triggerCalculate(selections.optionSelections, nextPresets, 'pick');
            },
            isPickingStyle: calculateMutation.isPending && activeKind === 'pick',
            unpickStyle: (styleGroupId, roomIdArg) => {
                const room = resolveRoom(roomIdArg);
                removePreset(styleGroupId, room);
                const nextPresets = selections.presetSelections.filter(
                    (s) => !(s.presetGroupId === styleGroupId && s.roomId === room)
                );
                triggerCalculate(selections.optionSelections, nextPresets, 'unpick');
            },
            isUnpickingStyle: calculateMutation.isPending && activeKind === 'unpick',
        };
    }, [selections, setOption, setOptions, removeOption, setPreset, removePreset, calculateMutation, activeKind, pathParams, urlRoomId, selectedDeadlineId, calcDeadlineId]);

    return <ConfiguratorMutationProvider value={value}>{children}</ConfiguratorMutationProvider>;
};
